import { useState, useEffect } from 'react';
import Sidebar from '../home/components/Sidebar';
import Navbar from '../home/components/Navbar';
import { getDatabaseTables, getTableStructure, getTableData } from '../../services/database';

export default function DatabaseManagerPage() {
  const [activeMenu, setActiveMenu] = useState('database-manager');
  const [tables, setTables] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [tableStructure, setTableStructure] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [loadingTable, setLoadingTable] = useState(false);

  // 加载所有表
  useEffect(() => {
    loadTables();
  }, []);

  const loadTables = async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await getDatabaseTables();
      if (result.success) {
        setTables(result.data);
      } else {
        setError(`加载表失败: 未知错误`);
      }
    } catch (err) {
      setError(`加载表异常: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  // 查看表详情
  const handleViewTable = async (tableName: string) => {
    setSelectedTable(tableName);
    setLoadingTable(true);
    setTableStructure([]);
    setTableData([]);
    
    try {
      // 加载表结构
      const structureResult = await getTableStructure(tableName);
      if (structureResult.success) {
        setTableStructure(structureResult.data);
      }

      // 加载表数据
      const dataResult = await getTableData(tableName);
      if (dataResult.success) {
        setTableData(dataResult.data);
      }
    } catch (err) {
      setError(`加载表详情失败: ${(err as Error).message}`);
    } finally {
      setLoadingTable(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex">
      <Sidebar activeMenu={activeMenu} onMenuChange={setActiveMenu} />
      
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        <main className="flex-1 main-content-padding overflow-y-auto overflow-x-hidden">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl font-bold mb-2">数据库管理</h1>
            <p className="text-gray-400 text-sm mb-8">
              查询并管理Supabase数据库中的表结构
            </p>

            {/* 错误信息 */}
            {error && (
              <div className="bg-red-900/30 border border-red-800 rounded-xl p-4 mb-6">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* 左侧：表列表 */}
              <div className="lg:col-span-1">
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800 h-full">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">数据库表</h3>
                    <button
                      onClick={loadTables}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-colors"
                    >
                      刷新
                    </button>
                  </div>

                  {loading ? (
                    <div className="text-center py-8">
                      <p className="text-gray-400 text-sm">加载中...</p>
                    </div>
                  ) : tables.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-400 text-sm">暂无表数据</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {tables.map((table, index) => (
                        <button
                          key={index}
                          onClick={() => handleViewTable(table.table_name)}
                          className={`w-full text-left px-4 py-3 rounded-lg text-sm transition-colors ${
                            selectedTable === table.table_name
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                          }`}
                        >
                          {table.table_name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* 右侧：表详情 */}
              <div className="lg:col-span-2">
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800 h-full">
                  {selectedTable ? (
                    <>
                      <h3 className="text-lg font-semibold mb-4">{selectedTable}</h3>
                      
                      {loadingTable ? (
                        <div className="text-center py-8">
                          <p className="text-gray-400 text-sm">加载表详情中...</p>
                        </div>
                      ) : (
                        <div className="space-y-6">
                          {/* 表结构 */}
                          <div>
                            <h4 className="text-md font-medium mb-3 text-gray-300">表结构</h4>
                            {tableStructure.length > 0 ? (
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b border-gray-700">
                                      <th className="py-2 px-4 text-left text-gray-400">字段名</th>
                                      <th className="py-2 px-4 text-left text-gray-400">数据类型</th>
                                      <th className="py-2 px-4 text-left text-gray-400">可空</th>
                                      <th className="py-2 px-4 text-left text-gray-400">默认值</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {tableStructure.map((column, index) => (
                                      <tr key={index} className="border-b border-gray-800">
                                        <td className="py-2 px-4 text-gray-300">{column.column_name}</td>
                                        <td className="py-2 px-4 text-gray-300">{column.data_type}</td>
                                        <td className="py-2 px-4 text-gray-300">{column.is_nullable}</td>
                                        <td className="py-2 px-4 text-gray-300">{column.column_default || '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p className="text-gray-400 text-sm">暂无表结构数据</p>
                            )}
                          </div>

                          {/* 表数据 */}
                          <div>
                            <h4 className="text-md font-medium mb-3 text-gray-300">表数据 (前10条)</h4>
                            {tableData.length > 0 ? (
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b border-gray-700">
                                      {Object.keys(tableData[0]).map((key) => (
                                        <th key={key} className="py-2 px-4 text-left text-gray-400">
                                          {key}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {tableData.map((row, index) => (
                                      <tr key={index} className="border-b border-gray-800">
                                        {Object.values(row).map((value, colIndex) => (
                                          <td key={colIndex} className="py-2 px-4 text-gray-300">
                                            {typeof value === 'object' && value !== null
                                              ? JSON.stringify(value) as string
                                              : String(value || '-')}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p className="text-gray-400 text-sm">暂无表数据</p>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-16">
                      <p className="text-gray-400 text-sm">请选择一个表查看详情</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}